import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Création dossier V3 — ultra-rapide, friction minimale.
 *  Champs visibles :
 *   - Nom du dossier (obligatoire — fusionné avec client_name)
 *   - Apporteur (optionnel, autocomplete depuis le registry)
 *   - Informations Client (optionnel, repliable) : emprunteur 1 & 2 + DOB
 *  Champs forcés en backend :
 *   - type_contact = "a_appeler"
 *   - etoiles = 1, status = "en_cours", etape = "prospect"
 */
export default function NewDossierDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    nom: "", apporteur: "",
    client_1_nom: "", client_1_prenom: "", client_1_dob: "",
    client_2_nom: "", client_2_prenom: "", client_2_dob: "",
  });
  const [showClient, setShowClient] = useState(false);
  const [apporteurOptions, setApporteurOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nom: "", apporteur: "",
        client_1_nom: "", client_1_prenom: "", client_1_dob: "",
        client_2_nom: "", client_2_prenom: "", client_2_dob: "",
      });
      setShowClient(false);
      // Charge le registry pour l'autocomplete
      (async () => {
        try {
          const res = await api.get("/apporteurs/registry");
          const all = [...(res.data.registered || []), ...(res.data.implicit || [])];
          setApporteurOptions(all.map((a) => a.name));
        } catch { /* noop */ }
      })();
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.nom.trim()) { toast.error("Nom du dossier requis"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/dossiers", {
        nom: form.nom.trim(),
        // nom = client_name (fusion V3)
        client_name: form.nom.trim(),
        apporteur: form.apporteur.trim() || null,
        // V3 — Informations Client
        client_1_nom: form.client_1_nom.trim() || null,
        client_1_prenom: form.client_1_prenom.trim() || null,
        client_1_dob: form.client_1_dob || null,
        client_2_nom: form.client_2_nom.trim() || null,
        client_2_prenom: form.client_2_prenom.trim() || null,
        client_2_dob: form.client_2_dob || null,
      });
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, calc(env(safe-area-inset-bottom) + 1rem))",
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col"
        style={{ maxHeight: "calc(100dvh - 2rem - env(safe-area-inset-bottom))" }}
        data-testid="new-dossier-dialog"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="font-display text-lg font-extrabold text-slate-900">Nouveau dossier</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100"
            data-testid="new-dossier-close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Nom du dossier <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              autoFocus
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="Ex : Dupont Jean & Marie"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[14px] focus:border-[color:var(--cf-primary)] focus:outline-none"
              data-testid="new-nom"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Apporteur
            </label>
            <input
              type="text"
              list="apporteur-list"
              value={form.apporteur}
              onChange={(e) => setForm({ ...form, apporteur: e.target.value })}
              placeholder="Optionnel — qui vous a apporté ce dossier ?"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[14px] focus:border-[color:var(--cf-primary)] focus:outline-none"
              data-testid="new-apporteur"
            />
            <datalist id="apporteur-list">
              {apporteurOptions.map((name) => <option key={name} value={name} />)}
            </datalist>
          </div>

          {/* Informations Client — repliable */}
          <button
            type="button"
            onClick={() => setShowClient((v) => !v)}
            className="w-full text-left flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-[12px] font-bold text-slate-600 hover:bg-slate-100"
            data-testid="toggle-client-info"
          >
            <span>Informations client {showClient ? "−" : "+"}</span>
            <span className="text-[10px] text-slate-400 font-semibold">Optionnel</span>
          </button>

          {showClient && (
            <div className="space-y-3 px-1" data-testid="client-info-section">
              {/* Emprunteur 1 */}
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Emprunteur 1</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Prénom"
                    value={form.client_1_prenom}
                    onChange={(e) => setForm({ ...form, client_1_prenom: e.target.value })}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                    data-testid="new-c1-prenom"
                  />
                  <input
                    type="text"
                    placeholder="Nom"
                    value={form.client_1_nom}
                    onChange={(e) => setForm({ ...form, client_1_nom: e.target.value })}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                    data-testid="new-c1-nom"
                  />
                </div>
                <input
                  type="date"
                  value={form.client_1_dob}
                  onChange={(e) => setForm({ ...form, client_1_dob: e.target.value })}
                  className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                  data-testid="new-c1-dob"
                />
              </div>
              {/* Emprunteur 2 */}
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Emprunteur 2</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Prénom"
                    value={form.client_2_prenom}
                    onChange={(e) => setForm({ ...form, client_2_prenom: e.target.value })}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                    data-testid="new-c2-prenom"
                  />
                  <input
                    type="text"
                    placeholder="Nom"
                    value={form.client_2_nom}
                    onChange={(e) => setForm({ ...form, client_2_nom: e.target.value })}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                    data-testid="new-c2-nom"
                  />
                </div>
                <input
                  type="date"
                  value={form.client_2_dob}
                  onChange={(e) => setForm({ ...form, client_2_dob: e.target.value })}
                  className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2 text-[13px] focus:border-[color:var(--cf-primary)] focus:outline-none"
                  data-testid="new-c2-dob"
                />
              </div>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-100 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200"
            data-testid="new-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="flex-[2] py-2.5 rounded-xl bg-[color:var(--cf-primary)] text-white font-bold text-sm hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-50"
            data-testid="new-submit"
          >
            {submitting ? "Création..." : "Créer le dossier"}
          </button>
        </footer>
      </div>
    </div>
  );
}
